<template>
  <div ref="containerRef" class="three-d-background"></div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import * as THREE from 'three'
import { gsap } from 'gsap'

const containerRef = ref<HTMLDivElement | null>(null)
let scene: THREE.Scene
let camera: THREE.PerspectiveCamera
let renderer: THREE.WebGLRenderer
let particles: THREE.Points
let animationFrameId: number

const initThreeJS = () => {
  if (!containerRef.value) return

  // Scene setup
  scene = new THREE.Scene()

  // Camera setup
  camera = new THREE.PerspectiveCamera(
    75,
    window.innerWidth / window.innerHeight,
    0.1,
    1000
  )
  camera.position.z = 30

  // Renderer setup
  renderer = new THREE.WebGLRenderer({
    alpha: true,
    antialias: true
  })
  renderer.setSize(window.innerWidth, window.innerHeight)
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  containerRef.value.appendChild(renderer.domElement)

  // Create floating geometric shapes
  createFloatingShapes()

  // Create particle system
  createParticles()

  // Lights
  const ambientLight = new THREE.AmbientLight(0xffffff, 0.5)
  scene.add(ambientLight)

  const pointLight = new THREE.PointLight(0x667eea, 1)
  pointLight.position.set(10, 10, 10)
  scene.add(pointLight)

  const pointLight2 = new THREE.PointLight(0x764ba2, 1)
  pointLight2.position.set(-10, -10, 10)
  scene.add(pointLight2)

  // Animation loop
  animate()

  // Handle window resize
  window.addEventListener('resize', onWindowResize)
}

const createFloatingShapes = () => {
  const geometries = [
    new THREE.OctahedronGeometry(2, 0),
    new THREE.TetrahedronGeometry(2, 0),
    new THREE.IcosahedronGeometry(2, 0),
    new THREE.TorusGeometry(1.5, 0.5, 16, 100)
  ]

  const material = new THREE.MeshStandardMaterial({
    color: 0x667eea,
    wireframe: true,
    transparent: true,
    opacity: 0.3
  })

  geometries.forEach((geometry, index) => {
    const mesh = new THREE.Mesh(geometry, material.clone())

    // Random position
    mesh.position.x = (Math.random() - 0.5) * 50
    mesh.position.y = (Math.random() - 0.5) * 50
    mesh.position.z = (Math.random() - 0.5) * 30

    // Random rotation
    mesh.rotation.x = Math.random() * Math.PI
    mesh.rotation.y = Math.random() * Math.PI

    scene.add(mesh)

    // Animate with GSAP
    gsap.to(mesh.rotation, {
      x: mesh.rotation.x + Math.PI * 2,
      y: mesh.rotation.y + Math.PI * 2,
      duration: 20 + index * 5,
      repeat: -1,
      ease: 'none'
    })

    gsap.to(mesh.position, {
      y: mesh.position.y + (Math.random() > 0.5 ? 10 : -10),
      duration: 10 + index * 3,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut'
    })
  })
}

const createParticles = () => {
  const particlesGeometry = new THREE.BufferGeometry()
  const particlesCount = 1000

  const positions = new Float32Array(particlesCount * 3)
  const colors = new Float32Array(particlesCount * 3)

  const color1 = new THREE.Color(0x667eea)
  const color2 = new THREE.Color(0x764ba2)

  for (let i = 0; i < particlesCount * 3; i += 3) {
    positions[i] = (Math.random() - 0.5) * 100
    positions[i + 1] = (Math.random() - 0.5) * 100
    positions[i + 2] = (Math.random() - 0.5) * 50

    const mixedColor = color1.clone().lerp(color2, Math.random())
    colors[i] = mixedColor.r
    colors[i + 1] = mixedColor.g
    colors[i + 2] = mixedColor.b
  }

  particlesGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  particlesGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))

  const particlesMaterial = new THREE.PointsMaterial({
    size: 0.15,
    vertexColors: true,
    transparent: true,
    opacity: 0.6,
    blending: THREE.AdditiveBlending
  })

  particles = new THREE.Points(particlesGeometry, particlesMaterial)
  scene.add(particles)
}

const animate = () => {
  animationFrameId = requestAnimationFrame(animate)

  // Rotate particles slowly
  if (particles) {
    particles.rotation.y += 0.0005
    particles.rotation.x += 0.0002
  }

  // Render scene
  renderer.render(scene, camera)
}

const onWindowResize = () => {
  camera.aspect = window.innerWidth / window.innerHeight
  camera.updateProjectionMatrix()
  renderer.setSize(window.innerWidth, window.innerHeight)
}

// Mouse interaction
const onMouseMove = (event: MouseEvent) => {
  if (!camera) return

  const mouseX = (event.clientX / window.innerWidth) * 2 - 1
  const mouseY = -(event.clientY / window.innerHeight) * 2 + 1

  gsap.to(camera.position, {
    x: mouseX * 5,
    y: mouseY * 5,
    duration: 1,
    ease: 'power2.out'
  })
}

onMounted(() => {
  initThreeJS()
  window.addEventListener('mousemove', onMouseMove)
})

onUnmounted(() => {
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId)
  }
  window.removeEventListener('resize', onWindowResize)
  window.removeEventListener('mousemove', onMouseMove)
  if (renderer && containerRef.value) {
    containerRef.value.removeChild(renderer.domElement)
    renderer.dispose()
  }
})
</script>

<style scoped lang="scss">
.three-d-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;

  canvas {
    display: block;
    width: 100%;
    height: 100%;
  }
}
</style>
